import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaBuilding,
  FaLeaf,
  FaSchool,
  FaChalkboardTeacher,
  FaBookOpen,
  FaAward,
  FaHeart,
  FaFutbol,
  FaCheckCircle,
} from 'react-icons/fa';
import AboutImage  from '../../assets/images/section/about.jpg'

const AboutSection = () => {
  return (
    <section className="py-8 bg-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex flex-col items-start gap-6 lg:flex-row lg:gap-8">
          
          {/* Left Column - Image and Cards */}
          <div className="w-full lg:w-1/2">
            <div className="relative">
              {/* Main Image */}
              <div className="overflow-hidden rounded-lg shadow-md">
                <img 
                  alt="Gyan Rashmi School Campus" 
                  className="object-cover w-full h-auto transition-transform duration-300 hover:scale-105" 
                  src={AboutImage}
                />
              </div>
              
              {/* Info Cards */}
              <div className="grid grid-cols-1 gap-3 mt-4 md:grid-cols-2">
                {/* Modern Infrastructure Card */}
                <div className="p-3 border border-gray-100 rounded-lg bg-blue-50">
                  <div className="flex items-start gap-2">
                    <div className="p-1.5 rounded-md bg-blue-50 text-blue-600">
                      <FaBuilding className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-0.5 text-xs font-semibold text-gray-800">Modern Infrastructure</h4>
                      <p className="text-xs leading-tight text-gray-600">
                        State-of-the-art classrooms, labs, and sports facilities
                      </p>
                    </div>
                  </div>
                </div>

                {/* Green Campus Card */}
                <div className="p-3 border border-gray-100 rounded-lg bg-emerald-50">
                  <div className="flex items-start gap-2">
                    <div className="p-1.5 rounded-md bg-emerald-50 text-emerald-600">
                      <FaLeaf className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-0.5 text-xs font-semibold text-gray-800">Green Campus</h4>
                      <p className="text-xs leading-tight text-gray-600">
                        Eco-friendly environment with gardens and solar power
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - About Content */}
          <div className="w-full lg:w-1/2">
            <div className="max-w-2xl">
              {/* Badge */}
              <div className="inline-flex items-center px-2.5 py-0.5 mb-2 text-xs font-medium text-blue-600 rounded-full bg-blue-50">
                <span className="w-1.5 h-1.5 mr-1.5 bg-blue-500 rounded-full"></span>
                About Our School
              </div>

              {/* Title */}
              <h2 className="mb-3 text-xl font-bold text-gray-900 md:text-2xl">
                Gyan Rashmi
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> (1994)</span>
              </h2>

              {/* Description */}
              <div className="mb-4 space-y-2 text-gray-600">
                <p className="text-sm leading-relaxed">
                  Welcome to Gyan Rashmi, where education meets excellence. 
                  Established in 1994, we have been at the forefront of providing quality 
                  education that blends traditional values with modern teaching methodologies.
                </p>
                <p className="text-sm leading-relaxed">
                  Our mission is to create a nurturing environment where every student can 
                  discover his/her potential, develop critical & innovative thinking skills, 
                  and grow into responsible global citizen.
                </p>
                <p className="text-sm leading-relaxed">
                  We believe in holistic development, combining academic rigor with 
                  co-curricular activities to ensure well-rounded growth for every child.
                </p>
              </div>

              {/* Feature Grid */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {/* CBSE Affiliated */}
                <div className="flex items-center gap-1.5 p-1.5 rounded-md bg-gray-50">
                  <div className="p-1 text-blue-600 bg-white border border-gray-100 rounded">
                    <FaSchool className="w-4 h-4" />
                  </div>
                  <span className="flex-1 text-xs font-medium text-gray-700">CBSE Affiliated School</span>
                </div>

                {/* Experienced Faculties */}
                <div className="flex items-center gap-1.5 p-1.5 rounded-md bg-gray-50">
                  <div className="p-1 bg-white border border-gray-100 rounded text-emerald-600">
                    <FaChalkboardTeacher className="w-4 h-4" />
                  </div>
                  <span className="flex-1 text-xs font-medium text-gray-700">Experienced Faculties</span>
                </div>

                {/* Holistic Curriculum */}
                <div className="flex items-center gap-1.5 p-1.5 rounded-md bg-gray-50">
                  <div className="p-1 text-purple-600 bg-white border border-gray-100 rounded">
                    <FaBookOpen className="w-4 h-4" />
                  </div>
                  <span className="flex-1 text-xs font-medium text-gray-700">Holistic Curriculum</span>
                </div>

                {/* Years Excellence */}
                <div className="flex items-center gap-1.5 p-1.5 rounded-md bg-gray-50">
                  <div className="p-1 bg-white border border-gray-100 rounded text-amber-600">
                    <FaAward className="w-4 h-4" />
                  </div>
                  <span className="flex-1 text-xs font-medium text-gray-700">31+ Years Excellence</span>
                </div>

                {/* Nurturing Environment */}
                <div className="flex items-center gap-1.5 p-1.5 rounded-md bg-gray-50">
                  <div className="p-1 bg-white border border-gray-100 rounded text-rose-600">
                    <FaHeart className="w-4 h-4" />
                  </div>
                  <span className="flex-1 text-xs font-medium text-gray-700">Nurturing Environment</span>
                </div>

                {/* Sports & Arts */}
                <div className="flex items-center gap-1.5 p-1.5 rounded-md bg-gray-50">
                  <div className="p-1 text-indigo-600 bg-white border border-gray-100 rounded">
                    <FaFutbol className="w-4 h-4" />
                  </div>
                  <span className="flex-1 text-xs font-medium text-gray-700">Focus on Sports & Arts</span>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 p-4 mb-4 border border-blue-100 rounded-lg md:grid-cols-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">100%</div>
                  <div className="text-xs text-gray-600">Board Results 2025</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">10K+</div>
                  <div className="text-xs text-gray-600">Happy Students</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">50+</div>
                  <div className="text-xs text-gray-600">Expert Faculties</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">31+</div>
                  <div className="text-xs text-gray-600">Years Experience</div>
                </div>
              </div>

              {/* Key Highlights */}
              <div className="p-3 mb-4 rounded-lg bg-gray-50">
                <h3 className="mb-2 text-xs font-semibold text-gray-800">Key Highlights</h3>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {/* Digital Classrooms */}
                  <div className="flex items-start gap-1.5">
                    <FaCheckCircle className="w-3 h-3 mt-0.5 text-green-500 flex-shrink-0" />
                    <span className="text-xs leading-tight text-gray-600">Digital classrooms with smart boards</span>
                  </div>

                  {/* Labs */}
                  <div className="flex items-start gap-1.5">
                    <FaCheckCircle className="w-3 h-3 mt-0.5 text-green-500 flex-shrink-0" />
                    <span className="text-xs leading-tight text-gray-600">Well-equipped science, computer & Robotics labs</span>
                  </div>

                  {/* Library */}
                  <div className="flex items-start gap-1.5">
                    <FaCheckCircle className="w-3 h-3 mt-0.5 text-green-500 flex-shrink-0" />
                    <span className="text-xs leading-tight text-gray-600">Spacious library with 10,000+ books</span>
                  </div>

                  {/* Sports Facilities */}
                  <div className="flex items-start gap-1.5">
                    <FaCheckCircle className="w-3 h-3 mt-0.5 text-green-500 flex-shrink-0" />
                    <span className="text-xs leading-tight text-gray-600">Indoor and outdoor sports facilities</span>
                  </div>

                  {/* Art Studios */}
                  <div className="flex items-start gap-1.5">
                    <FaCheckCircle className="w-3 h-3 mt-0.5 text-green-500 flex-shrink-0" />
                    <span className="text-xs leading-tight text-gray-600">Music, dance, and art studios</span>
                  </div>

                  {/* Workshops */}
                  <div className="flex items-start gap-1.5">
                    <FaCheckCircle className="w-3 h-3 mt-0.5 text-green-500 flex-shrink-0" />
                    <span className="text-xs leading-tight text-gray-600">Regular workshops and seminars</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;